import React, { CSSProperties, Fragment, useRef, useState } from 'react';
import { styled, css } from '../../../../styles';
import { filterNotEmpty } from '../../../../utils/listHelpers';
import { Tooltip, shouldEnableTooltip } from '../../../Tooltip/Tooltip';
import { SelectMenuItemChip } from './SelectMenuItemChip';
import { Bullet } from './Bullet';
import { SelectV2Types } from '../../SelectV2.types';

interface SecondaryLabelItemProps {
  option: SelectV2Types.OptionTypeWithCode;
  visible?: Pick<
    Required<SelectV2Types.SingleSelectProps<unknown>>['visibleElementsWhenSelected'],
    'subLabel' | 'secondarySubLabel' | 'chip' | 'code'
  >;
  codeInBrackets?: boolean;
  style?: CSSProperties;
  variant?: 'small' | 'medium';
  startAdornment?: (elements: SecondaryLabelItem[]) => JSX.Element | null;
  noWrapSubLabel: boolean;
  maxSecondarySubLabelLength?: number;
}

interface SecondaryLabelItem {
  id: string;
  content: React.ReactNode | string;
  type: 'code' | 'chip' | 'sub-label';
}

export const SecondaryLabels: React.FC<React.PropsWithChildren<SecondaryLabelItemProps>> = ({
  option,
  visible = { subLabel: true, secondarySubLabel: true, chip: true, code: true },
  codeInBrackets,
  startAdornment,
  noWrapSubLabel,
  variant = 'medium',
  maxSecondarySubLabelLength
}) => {
  const subLabels = visible.subLabel && option.subLabel?.length ? option.subLabel : [];
  const [showTooltip, setTooltipState] = useState(false);
  const secondarySubLabelRef = useRef<HTMLDivElement>(null);

  const CodeWithTooltip = ({ codeInBrackets }: { codeInBrackets?: boolean }) => (
    <Tooltip title={option.code ?? ''} placement="top" disabled={!showTooltip}>
      <StyledEllipsis
        className={variant}
        onMouseEnter={(e) => {
          setTooltipState((prevState) => {
            if (prevState) {
              return true;
            }
            return shouldEnableTooltip(e.target as HTMLSpanElement);
          });
        }}
      >
        {codeInBrackets ? `(${option.code})` : option.code}
      </StyledEllipsis>
    </Tooltip>
  );

  const secondaryLabels = filterNotEmpty<SecondaryLabelItem>([
    option.code && visible.code
      ? {
          id: option.code,
          content: <CodeWithTooltip codeInBrackets={codeInBrackets} />,
          type: 'code'
        }
      : undefined,

    option.chip?.title && visible.chip
      ? {
          id: option.chip.title,
          content: <SelectMenuItemChip {...option.chip} />,
          type: 'chip'
        }
      : undefined,

    ...(subLabels.map((text) => ({
      id: text?.toString() ?? '',
      content: text,
      type: 'sub-label' as const
    })) ?? [])
  ]);

  return (
    <>
      {!!startAdornment && startAdornment(secondaryLabels)}
      {secondaryLabels.length !== 0 && (
        <StyledSecondaryLabels $columnCount={2 * secondaryLabels.length - 1}>
          {secondaryLabels.map((secondaryLabel, i) => (
            <Fragment key={secondaryLabel.id}>
              {i !== 0 && <Bullet style={{ textOverflow: 'clip' }} />}
              <StyledSecondaryLabelItem $noWrap={noWrapSubLabel} className={`${secondaryLabel.type} ${variant}`}>
                {secondaryLabel.content}
              </StyledSecondaryLabelItem>
            </Fragment>
          ))}
        </StyledSecondaryLabels>
      )}

      {visible.secondarySubLabel && option.secondarySubLabel && (
        <>
          <Bullet style={{ textOverflow: 'clip' }} />
          <Tooltip title={option.secondarySubLabel} placement="right" disabled={!showTooltip}>
            <StyledEllipsis
              ref={secondarySubLabelRef}
              onMouseEnter={() => setTooltipState(shouldEnableTooltip(secondarySubLabelRef.current))}
            >
              {typeof maxSecondarySubLabelLength === 'number'
                ? option.secondarySubLabel.slice(0, maxSecondarySubLabelLength)
                : option.secondarySubLabel}
            </StyledEllipsis>
          </Tooltip>
        </>
      )}
    </>
  );
};

const StyledSecondaryLabels = styled('span')<{ $columnCount: number }>`
  && {
    display: inline-grid;
    grid-template-columns: repeat(${({ $columnCount }) => $columnCount}, minmax(0, auto));
    align-items: center;
    justify-content: flex-start;
  }
`;

const StyledSecondaryLabelItem = styled('span')<{ $noWrap: boolean }>`
  &.code {
    color: rgba(0, 0, 0, 0.54);
    font-weight: 400;

    &.small {
      font-size: 13px;
      width: 100%;
    }
  }

  &.sub-label {
    font-size: 13px;
    ${({ $noWrap }) =>
      $noWrap &&
      css`
        height: 16px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      `}
    color: ${({ theme }) => theme.tokens.colorOnSurface};
  }
`;

const StyledEllipsis = styled('span')`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
